import * as React from 'react'
import { observer } from 'mobx-react'
import { expr } from 'mobx-utils'
import cx from 'classnames'
import Button from 'components/Button'
import {
  ModalSheet,
  ModalSheetHeader,
  ModalSheetContent,
  ModalSheetContentTitle,
  ModalSheetContentDescription,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetContentBlock,
} from 'components/ModalSheet'
import ContentLoading from 'components/ContentLoading'
import ContentError from 'components/ContentError'
import { Form, FormGroupContainer } from 'components/inputs'
import { useFocusTracking } from 'hooks/focus-hooks'
import { extractMessageFromError } from 'utils/error-util'
import CouponSelectionViewModel from '../../../../CouponSelectionViewModel'
import { CouponList, CouponListItem } from 'billing/components/CouponList'
import { Id } from 'types/definitions'
import Coupon from 'billing/models/Coupon'
import { useTask } from 'hooks/mobx-hooks'
import styles from './CouponModal.sass'

interface CouponModalProps {
  vm: CouponSelectionViewModel
}

export default observer(function CouponModal({ vm }: CouponModalProps) {
  return (
    <ModalSheet showing={vm.showing} onRequestClose={vm.close}>
      <ModalSheetHeader withCloseButton />
      <ModalSheetContent>
        {vm.fetchCoupons.match({
          pending: () => <ContentLoading compact />,
          rejected: (err) => <ContentError error={err} />,
          resolved: () => (
            <>
              {vm.coupons.length > 0 ? (
                <>
                  <ModalSheetContentBlock>
                    <ModalSheetContentTitle>
                      You have{' '}
                      {vm.coupons.length === 1
                        ? 'a coupon'
                        : `${vm.coupons.length} coupons`}
                    </ModalSheetContentTitle>
                    <ModalSheetContentDescription>
                      Select one from the list below, or add another one.
                    </ModalSheetContentDescription>
                  </ModalSheetContentBlock>
                  <ModalSheetContentBlock>
                    <CouponList>
                      {vm.coupons.map((coupon) => (
                        <CouponListItemContainer
                          key={Id.assert(coupon.id)}
                          coupon={coupon}
                          vm={vm}
                        />
                      ))}
                    </CouponList>
                  </ModalSheetContentBlock>

                  <Divider />
                </>
              ) : (
                <>
                  <ModalSheetContentBlock>
                    <ModalSheetContentTitle>
                      Have a coupon?
                    </ModalSheetContentTitle>
                    <ModalSheetContentDescription>
                      Enter the code below.
                    </ModalSheetContentDescription>
                  </ModalSheetContentBlock>
                </>
              )}
              <AddCouponSection vm={vm} />
            </>
          ),
        })}
      </ModalSheetContent>

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button onClick={vm.close}>Close</Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})

interface CouponListItemContainerProps {
  vm: CouponSelectionViewModel
  coupon: Coupon
}

const CouponListItemContainer = observer(
  ({ vm, coupon }: CouponListItemContainerProps) => {
    const select = useTask.resolved(() => vm.select(coupon), [vm, coupon])
    const isSelected = expr(() => vm.selected === coupon)
    return (
      <CouponListItem
        key={Id.assert(coupon.id)}
        coupon={coupon}
        data-test={`coupon-list-item-${coupon.code}`}
        selected={isSelected}
        onClick={isSelected ? undefined : select}
        busy={select.pending}
        disabled={vm.select.pending || vm.disabled}
      />
    )
  },
)

interface AddCouponSectionProps {
  vm: CouponSelectionViewModel
}

/**
 " Add Coupon Section
 */
const AddCouponSection = observer(function AddCouponSection({
  vm,
}: AddCouponSectionProps) {
  const onCodeChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => vm.setCode(e.target.value),
    [vm],
  )
  const [focusProps, isFocused] = useFocusTracking()
  const inputRef = React.useRef<HTMLInputElement | null>(null)
  const focus = React.useCallback(() => inputRef.current?.focus(), [])
  const disabled = vm.tasks.pending || vm.disabled
  const error = extractMessageFromError(vm.addCoupon.error)
  return (
    <ModalSheetContentBlock>
      <Form onSubmit={vm.canAdd ? vm.addCoupon : undefined}>
        <FormGroupContainer
          model={vm}
          field="codeToAdd"
          label="Add a new Coupon"
          error={error}
        >
          <div
            className={cx(
              styles.inputContainer,
              isFocused && styles.focused,
              !!error && styles.danger,
            )}
            onClick={focus}
          >
            <input
              type="text"
              autoCapitalize="off"
              value={vm.codeToAdd}
              onChange={onCodeChange}
              disabled={disabled}
              ref={inputRef}
              placeholder="Enter your code here."
              data-test="new-coupon-code-input"
              className={cx(
                styles.input,
                vm.codeToAdd.length > 0 && styles.filled,
              )}
              {...focusProps}
            />
            <Button
              submit
              loading={vm.addCoupon.pending}
              disabled={!vm.canAdd || disabled}
              dataTest="new-coupon-apply-button"
            >
              Apply
            </Button>
          </div>
        </FormGroupContainer>
      </Form>
    </ModalSheetContentBlock>
  )
})

/**
 " Divider
 */
function Divider() {
  return (
    <ModalSheetContentBlock>
      <div className={styles.divider}>
        <div className={styles.divider}>
          <div className={styles.dividerText}>OR</div>
        </div>
      </div>
    </ModalSheetContentBlock>
  )
}
